import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from 'xlsx';
import styles from './BudgetModal.module.css';
import {
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaChartLine,
  FaPlus,
  FaWallet,
  FaDollarSign,
  FaExclamationTriangle,
  FaTimes,
  FaSave,
  FaDownload,
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import AddBudgetModal from './AddBudgetModal';

const BudgetModal = ({ project, onClose }) => {
  const [income, setIncome] = useState('-');
  const [expense, setExpense] = useState('-');
  const [total, setTotal] = useState('-');
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isAddBudgetModalOpen, setIsAddBudgetModalOpen] = useState(false);

  const fetchProjectExpenses = async () => {
    if (!project?.id) return [];

    try {
      let allExpenses = [];

      // 1. Get all employees assigned to this project
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();
      const projectEmployees = projectData?.employees || [];

      // 2. For each employee, fetch their payments
      for (const employee of projectEmployees) {
        // 2.1 Fetch salary payments
        const salaryQuery = query(
          collection(db, 'Salaries'),
          where('employeeId', '==', employee.id)
        );
        const salarySnapshot = await getDocs(salaryQuery);

        // Process salary payments
        salarySnapshot.docs.forEach(doc => {
          const salaryData = doc.data();
          if (salaryData.payments) {
            Object.entries(salaryData.payments).forEach(([yearMonth, payment]) => {
              allExpenses.push({
                id: `${doc.id}-${yearMonth}`,
                employeeName: `${employee.name} ${employee.surname}`,
                paymentDate: payment.paidDate,
                type: 'Gider',
                category: 'Maaş Ödemesi',
                amount: payment.amount,
                description: `${yearMonth} dönemi maaş ödemesi`,
                paymentStatus: 'paid' // Maaş ödemeleri genellikle ödenmiş olarak kabul edilir
              });
            });
          }
        });

        // 2.2 Fetch other payments
        const paymentsQuery = query(
          collection(db, 'Payments'),
          where('employeeId', '==', employee.id),
          where('projectId', '==', project.id)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);

        // Process other payments
        paymentsSnapshot.docs.forEach(doc => {
          const paymentData = doc.data();
          allExpenses.push({
            id: doc.id,
            employeeName: `${employee.name} ${employee.surname}`,
            paymentDate: paymentData.paymentDate,
            type: 'Gider',
            category: paymentData.paymentType || 'Diğer Ödeme',
            amount: paymentData.paidAmount,
            description: paymentData.description,
            paymentStatus: paymentData.paymentStatus || 'paid' // Diğer ödemeler genellikle ödenmiş
          });
        });
      }

      // 3. Fetch budgets associated with this project
      const budgetsQuery = query(
        collection(db, 'Budgets'),
        where('projectId', '==', project.id)
      );
      const budgetsSnapshot = await getDocs(budgetsQuery);

      // Process budgets
      const employeeDetailsMap = new Map();
      for (const docSnap of budgetsSnapshot.docs) {
        const budgetData = docSnap.data();
        let employeeName = budgetData.BütçeSahibi || 'Belirsiz';

        // If BütçeSahibi looks like a Firestore document ID, fetch the employee's name
        if (/^[a-zA-Z0-9]{20,}$/.test(employeeName)) {
          try {
            if (!employeeDetailsMap.has(employeeName)) {
              const employeeDoc = await getDoc(doc(db, 'Employees', employeeName));
              if (employeeDoc.exists()) {
                const employeeData = employeeDoc.data();
                employeeDetailsMap.set(employeeName, `${employeeData.eName} ${employeeData.eSurname}`);
              }
            }
            employeeName = employeeDetailsMap.get(employeeName) || budgetData.BütçeSahibi;
          } catch (error) {
            console.error('Error fetching employee details:', error);
          }
        }

        allExpenses.push({
          id: docSnap.id,
          employeeName: employeeName,
          paymentDate: budgetData.BütçeTarihi,
          type: 'Gider',
          category: budgetData.BütçeKategorisi || 'Bütçe',
          amount: budgetData.BütçeMiktarı,
          description: budgetData.BütçeAdı,
          paymentStatus: budgetData.paymentStatus || 'unpaid' // Varsayılan olarak ödenmedi
        });
      }

      // 4. Sort expenses by date
      allExpenses.sort((a, b) =>
        new Date(b.paymentDate) - new Date(a.paymentDate)
      );

      // 5. Calculate total expenses
      const totalExpense = allExpenses.reduce((sum, expense) => {
        // Convert string to number if necessary and handle any potential decimal separators
        const amount = typeof expense.amount === 'string'
          ? Number(expense.amount.replace(/\./g, '').replace(',', '.'))
          : expense.amount;
        return sum + amount;
      }, 0);

      setExpense(new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(totalExpense) + ' TL');

      return allExpenses;
    } catch (error) {
      console.error('Error fetching project expenses:', error);
      toast.error('Gider bilgileri alınırken bir hata oluştu');
      return [];
    }
  };

  const fetchProjectIncome = async () => {
    if (!project?.id) return [];

    try {
      // Placeholder for income fetching logic
      // For now, we'll just return an empty array
      // In a real implementation, you would query the incomes collection
      const dummyIncomeTotal = 0;

      setIncome(new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(dummyIncomeTotal) + ' TL');

      return [];
    } catch (error) {
      console.error('Error fetching project income:', error);
      toast.error('Gelir bilgileri alınırken bir hata oluştu');
      return [];
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const expenses = await fetchProjectExpenses();
      const incomes = await fetchProjectIncome();

      setBudgets(expenses);
      setIncomeRecords(incomes);

      // Calculate and set total (income - expenses)
      const totalIncome = incomes.reduce((sum, income) => sum + (parseFloat(income.amount) || 0), 0);
      const totalExpense = expenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0);
      const totalBalance = totalIncome - totalExpense;

      setTotal(new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: 'always'
      }).format(totalBalance) + ' TL');
    } catch (error) {
      console.error('Error loading budget data:', error);
      toast.error('Bütçe verileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' TL';
  };

  const handleAddBudget = () => {
    setIsAddBudgetModalOpen(true);
  };

  const handleBudgetSubmit = async (budgetData) => {
    // Refresh the budgets list after adding a new budget
    await loadData();
  };

  const handlePaymentStatusChange = async (budgetId, newStatus) => {
    try {
      // Bütçe kaydını güncelle
      const budgetRef = doc(db, 'Budgets', budgetId);
      await updateDoc(budgetRef, {
        paymentStatus: newStatus,
        lastUpdated: new Date().toISOString()
      });

      // Yerel state'i güncelle
      setBudgets(prevBudgets =>
        prevBudgets.map(budget =>
          budget.id === budgetId
            ? { ...budget, paymentStatus: newStatus }
            : budget
        )
      );

      toast.success(`Ödeme durumu "${newStatus === 'paid' ? 'Ödendi' : 'Ödenmedi'}" olarak güncellendi`);
    } catch (error) {
      console.error('Ödeme durumu güncellenirken hata:', error);
      toast.error('Ödeme durumu güncellenemedi');
    }
  };

  const handleDeleteExpense = async (budgetId, expenseDescription) => {
    if (!window.confirm(`"${expenseDescription}" gider kaydını silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      // Bütçe kaydını sil
      await deleteDoc(doc(db, 'Budgets', budgetId));

      // Yerel state'i güncelle
      setBudgets(prevBudgets => prevBudgets.filter(budget => budget.id !== budgetId));

      toast.success('Gider kaydı başarıyla silindi');

      // Verileri yeniden yükle (toplamları güncellemek için)
      await loadData();
    } catch (error) {
      console.error('Gider silinirken hata:', error);
      toast.error('Gider kaydı silinemedi');
    }
  };

  useEffect(() => {
    loadData();
  }, [project]);

  const handleDownloadExcel = () => {
    try {
      // Tarih aralığına göre filtrelenmiş verileri al (güncel state'den)
      const filteredIncomes = filterByDateRange(incomeRecords, startDate, endDate);
      const filteredExpenses = filterByDateRange(budgets, startDate, endDate);

      // Yeni bir workbook oluştur
      const workbook = XLSX.utils.book_new();

      // Tüm veriler için tek sayfa oluştur (yatay düzen)
      const allData = [];

      // Başlık ve proje bilgileri
      allData.push(['PROJE BÜTÇE RAPORU - ' + (project.name || 'Belirsiz')]);
      allData.push(['Rapor Tarihi: ' + new Date().toLocaleDateString('tr-TR')]);

      // Tarih aralığı bilgisi
      if (startDate && endDate) {
        allData.push(['Tarih Aralığı: ' + `${startDate.toLocaleDateString('tr-TR')} - ${endDate.toLocaleDateString('tr-TR')}`]);
      } else {
        allData.push(['Tarih Aralığı: Tüm Kayıtlar']);
      }

      allData.push([]); // Boş satır

      // Özet bilgiler (yatay düzende)
      allData.push(['ÖZET BİLGİLER']);
      allData.push(['Toplam Gelir', 'Toplam Gider', 'Net Bakiye']);
      allData.push([
        income.replace(' TL', ''),
        expense.replace(' TL', ''),
        total.replace(' TL', '')
      ]);

      allData.push([]); // Boş satır
      allData.push([]); // Boş satır

      // Gelirler bölümü
      allData.push(['GELİRLER']);
      if (filteredIncomes.length > 0) {
        allData.push(['Tarih', 'Açıklama', 'Kategori', 'Tutar (TL)', '', '', '', '', '', '']); // Boş kolonlar için yer

        filteredIncomes.forEach(income => {
          const amount = typeof income.amount === 'number' ? income.amount : parseFloat(income.amount) || 0;
          allData.push([
            formatDate(income.date),
            income.description || 'Açıklama yok',
            income.category || 'Kategori yok',
            amount,
            '', '', '', '', '', '' // Boş kolonlar
          ]);
        });

        // Gelir toplamı
        const totalIncomeAmount = filteredIncomes.reduce((sum, income) => {
          const amount = typeof income.amount === 'number' ? income.amount : parseFloat(income.amount) || 0;
          return sum + amount;
        }, 0);

        allData.push(['', '', 'TOPLAM GELİR:', totalIncomeAmount, '', '', '', '', '', '']);
      } else {
        allData.push(['Gelir kaydı bulunamadı']);
      }

      allData.push([]); // Boş satır
      allData.push([]); // Boş satır

      // Giderler bölümü
      allData.push(['GİDERLER']);
      if (filteredExpenses.length > 0) {
        allData.push(['Tarih', 'Çalışan', 'Açıklama', 'Kategori', 'Tutar (TL)', 'Durum', '', '', '', '']);

        filteredExpenses.forEach(expense => {
          const amount = typeof expense.amount === 'string'
            ? Number(expense.amount.replace(/\./g, '').replace(',', '.'))
            : expense.amount;

          allData.push([
            formatDate(expense.paymentDate),
            expense.employeeName || 'Belirsiz',
            expense.description || 'Açıklama yok',
            expense.category || 'Kategori yok',
            amount || 0,
            expense.paymentStatus === 'paid' ? 'Ödendi' : 'Ödenmedi',
            '', '', '', '' // Boş kolonlar (modal ile tutarlılık için)
          ]);
        });

        // Gider toplamı
        const totalExpenseAmount = filteredExpenses.reduce((sum, expense) => {
          const amount = typeof expense.amount === 'string'
            ? Number(expense.amount.replace(/\./g, '').replace(',', '.'))
            : expense.amount;
          return sum + (amount || 0);
        }, 0);

        allData.push(['', '', '', 'TOPLAM GİDER:', totalExpenseAmount, '', '', '', '', '']);
      } else {
        allData.push(['Gider kaydı bulunamadı']);
      }

      allData.push([]); // Boş satır
      allData.push([]); // Boş satır

      // Detaylı analiz bölümü
      allData.push(['DETAYLI ANALİZ']);
      allData.push(['Kategori', 'Gelir Toplamı', 'Gider Toplamı', 'Net Fark', 'Yüzde', 'Durum', 'Açıklama', '', '', '']);

      // Kategori bazında analiz
      const categoryAnalysis = {};

      // Gelir kategorileri
      filteredIncomes.forEach(income => {
        const category = income.category || 'Diğer';
        const amount = typeof income.amount === 'number' ? income.amount : parseFloat(income.amount) || 0;
        if (!categoryAnalysis[category]) {
          categoryAnalysis[category] = { income: 0, expense: 0 };
        }
        categoryAnalysis[category].income += amount;
      });

      // Gider kategorileri
      filteredExpenses.forEach(expense => {
        const category = expense.category || 'Diğer';
        const amount = typeof expense.amount === 'string'
          ? Number(expense.amount.replace(/\./g, '').replace(',', '.'))
          : expense.amount;
        if (!categoryAnalysis[category]) {
          categoryAnalysis[category] = { income: 0, expense: 0 };
        }
        categoryAnalysis[category].expense += (amount || 0);
      });

      // Kategori analizi ekle
      Object.entries(categoryAnalysis).forEach(([category, data]) => {
        const netDiff = data.income - data.expense;
        const totalAmount = data.income + data.expense;
        const percentage = totalAmount > 0 ? ((netDiff / totalAmount) * 100).toFixed(2) : 0;
        const status = netDiff > 0 ? 'Pozitif' : netDiff < 0 ? 'Negatif' : 'Dengeli';

        allData.push([
          category,
          data.income,
          data.expense,
          netDiff,
          percentage + '%',
          status,
          netDiff > 0 ? 'Kar' : netDiff < 0 ? 'Zarar' : 'Dengeli',
          '', '', ''
        ]);
      });

      // Worksheet oluştur
      const worksheet = XLSX.utils.aoa_to_sheet(allData);

      // Kolon genişliklerini ayarla (yatay düzen için)
      const colWidths = [
        { wch: 15 }, // Tarih
        { wch: 25 }, // Açıklama/Çalışan
        { wch: 20 }, // Kategori
        { wch: 15 }, // Tutar
        { wch: 15 }, // Ek bilgi 1
        { wch: 15 }, // Ek bilgi 2
        { wch: 15 }, // Ek bilgi 3
        { wch: 15 }, // Ek bilgi 4
        { wch: 15 }, // Ek bilgi 5
        { wch: 15 }  // Ek bilgi 6
      ];
      worksheet['!cols'] = colWidths;

      // Sayfa düzenini yatay (landscape) olarak ayarla
      worksheet['!margins'] = {
        left: 0.7,
        right: 0.7,
        top: 0.75,
        bottom: 0.75,
        header: 0.3,
        footer: 0.3
      };

      // Yazdırma ayarları
      worksheet['!printSettings'] = {
        orientation: 'landscape',
        paperSize: 9, // A4
        scale: 85, // %85 ölçek
        fitToWidth: 1,
        fitToHeight: 0
      };

      // Worksheet'i workbook'a ekle
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Proje Bütçe Raporu');

      // Dosya adını oluştur
      const fileName = `${project.name || 'Proje'}_Detayli_Butce_Raporu_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(workbook, fileName);

      toast.success('Detaylı Excel raporu başarıyla indirildi!');
    } catch (error) {
      console.error('Excel indirme hatası:', error);
      toast.error('Excel dosyası indirilemedi. Lütfen tekrar deneyin.');
    }
  };

  const filterByDateRange = (records, startDate, endDate) => {
    if (!startDate || !endDate) return records;
    return records.filter(record => {
      const recordDate = new Date(record.paymentDate || record.date);
      return recordDate >= startDate && recordDate <= endDate;
    });
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaFileInvoiceDollar className={styles.modalIcon} />
            Proje Bütçe Yönetimi
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Budget Summary Cards */}
          <div className={styles.budgetCards}>
            <div className={`${styles.budgetCard} ${styles.incomeCard}`}>
              <div className={styles.cardIcon}>
                <FaMoneyBillWave />
              </div>
              <div className={styles.cardContent}>
                <p className={styles.cardTitle}>Toplam Gelir</p>
                <h3 className={styles.cardValue}>{income}</h3>
              </div>
            </div>

            <div className={`${styles.budgetCard} ${styles.expenseCard}`}>
              <div className={styles.cardIcon}>
                <FaWallet />
              </div>
              <div className={styles.cardContent}>
                <p className={styles.cardTitle}>Toplam Gider</p>
                <h3 className={styles.cardValue}>{expense}</h3>
              </div>
            </div>

            <div className={`${styles.budgetCard} ${styles.totalCard}`}>
              <div className={styles.cardIcon}>
                <FaChartLine />
              </div>
              <div className={styles.cardContent}>
                <p className={styles.cardTitle}>Toplam Bakiye</p>
                <h3 className={styles.cardValue}>{total}</h3>
              </div>
            </div>
          </div>

          {/* Header Actions */}
          <div className={styles.headerActions}>
            <div className={styles.dateRangeContainer}>
              <FaCalendarAlt className={styles.datePickerIcon} />
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Tarih Aralığı Seçin"
                className={styles.datePickerInput}
                isClearable={true}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownloadExcel}>
              <FaDownload className={styles.buttonIcon} />
              Excel İndir
            </button>
            <button className={styles.addButton} onClick={handleAddBudget}>
              <FaPlus className={styles.buttonIcon} />
              Yeni Bütçe Ekle
            </button>
          </div>

          {/* Budget Tables */}
          <div className={styles.budgetTablesContainer}>
            <div className={styles.budgetTables}>
              {/* Income Table */}
              <div className={styles.tableContainer}>
                <div className={`${styles.tableHeader} ${styles.incomeHeader}`}>
                  <h4>Gelirler</h4>
                </div>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Açıklama</th>
                        <th>Kategori</th>
                        <th>Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4">
                            <div className={styles.loadingIndicator}>
                              <div className={styles.loadingSpinner}></div>
                              <p>Yükleniyor...</p>
                            </div>
                          </td>
                        </tr>
                      ) : incomeRecords && incomeRecords.length > 0 ? (
                        filterByDateRange(incomeRecords, startDate, endDate).map((income, index) => (
                          <tr key={income.id || index}>
                            <td>{formatDate(income.date)}</td>
                            <td>{income.description || 'Açıklama yok'}</td>
                            <td>{income.category || 'Kategori yok'}</td>
                            <td>{formatCurrency(income.amount)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr className={styles.emptyRow}>
                          <td colSpan="4">
                            <div className={styles.noData}>
                              <FaDollarSign className={styles.noDataIcon} />
                              <p>Gelir kaydı bulunamadı</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Expense Table */}
              <div className={styles.tableContainer}>
                <div className={`${styles.tableHeader} ${styles.expenseHeader}`}>
                  <h4>Giderler</h4>
                </div>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Çalışan</th>
                        <th>Açıklama</th>
                        <th>Kategori</th>
                        <th>Tutar</th>
                        <th>Durum</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7">
                            <div className={styles.loadingIndicator}>
                              <div className={styles.loadingSpinner}></div>
                              <p>Yükleniyor...</p>
                            </div>
                          </td>
                        </tr>
                      ) : budgets && budgets.length > 0 ? (
                        filterByDateRange(budgets, startDate, endDate).map((budget, index) => (
                          <tr key={budget.id || index}>
                            <td>{formatDate(budget.paymentDate)}</td>
                            <td>{budget.employeeName || 'Belirsiz'}</td>
                            <td>{budget.description || 'Açıklama yok'}</td>
                            <td>{budget.category || 'Kategori yok'}</td>
                            <td>{formatCurrency(budget.amount)}</td>
                            <td>
                              <div className={styles.paymentStatusContainer}>
                                <button
                                  className={`${styles.statusButton} ${budget.paymentStatus === 'paid' ? styles.paidStatus : styles.unpaidStatus
                                    }`}
                                  onClick={() => handlePaymentStatusChange(
                                    budget.id,
                                    budget.paymentStatus === 'paid' ? 'unpaid' : 'paid'
                                  )}
                                  title={`Durumu değiştir: ${budget.paymentStatus === 'paid' ? 'Ödenmedi' : 'Ödendi'}`}
                                >
                                  {budget.paymentStatus === 'paid' ? (
                                    <>
                                      <FaCheck className={styles.statusIcon} />
                                      Ödendi
                                    </>
                                  ) : (
                                    <>
                                      <FaClock className={styles.statusIcon} />
                                      Ödenmedi
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                            <td>
                              <button
                                className={styles.deleteButton}
                                onClick={() => handleDeleteExpense(budget.id, budget.description || 'Bu gider')}
                                title="Gider kaydını sil"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className={styles.emptyRow}>
                          <td colSpan="7">
                            <div className={styles.noData}>
                              <FaExclamationTriangle className={styles.noDataIcon} />
                              <p>Gider kaydı bulunamadı</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
      {isAddBudgetModalOpen && (
        <AddBudgetModal
          isOpen={isAddBudgetModalOpen}
          onClose={() => setIsAddBudgetModalOpen(false)}
          onSubmit={handleBudgetSubmit}
          projectId={project.id}
        />
      )}
    </div>
  );
};

export default BudgetModal;